import type { ComparisonCriterion, ComparisonCriterionSide } from '@/types/endpointTests';

const MINUS = '−';

const percent = (value: number) => `${Math.round(value * 100)}%`;

function sideText(side: ComparisonCriterionSide): string {
  const parts = [
    side.passRate !== null ? percent(side.passRate) : null,
    side.meanScore !== null ? side.meanScore.toFixed(2) : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : '—';
}

function deltaText(value: number | null, kind: 'rate' | 'score'): string {
  if (value === null) return '—';
  if (value === 0) return kind === 'rate' ? '0%' : '0.00';
  const sign = value > 0 ? '+' : MINUS;
  const magnitude = Math.abs(value);
  return `${value > 0 ? '▲' : '▼'} ${sign}${kind === 'rate' ? percent(magnitude) : magnitude.toFixed(2)}`;
}

const deltaClass = (value: number | null) =>
  value === null || value === 0
    ? 'text-gray-500 dark:text-gray-400'
    : value < 0
      ? 'text-red-600 dark:text-red-400'
      : 'text-green-700 dark:text-green-400';

/** The size of a criterion's drop: its most negative available delta. */
const dropKey = (criterion: ComparisonCriterion) => {
  const deltas = [criterion.delta.passRate, criterion.delta.meanScore].filter((v): v is number => v !== null);
  return deltas.length > 0 ? Math.min(...deltas) : 0;
};

export function sortCriteriaByDrop(criteria: ComparisonCriterion[]): ComparisonCriterion[] {
  return [...criteria].sort((a, b) => dropKey(a) - dropKey(b));
}

/**
 * Per-criterion deltas (task 1180): for every criterion in both test runs, the
 * pass rate and mean score in each and the change, largest drop first — which
 * quality moved across the whole suite, which the row diff cannot say.
 */
export function ComparisonCriteriaPanel({
  criteria,
  aLabel,
  bLabel,
}: {
  criteria: ComparisonCriterion[];
  aLabel: string;
  bLabel: string;
}) {
  return (
    <section
      data-testid="comparison-criteria"
      className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
    >
      <div className="flex flex-wrap items-baseline gap-x-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">Criteria</h2>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          pass rate and mean score in each test · largest drop first
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
              <th className="px-4 py-2 font-medium">Criterion</th>
              <th className="px-4 py-2 font-medium">a · {aLabel}</th>
              <th className="px-4 py-2 font-medium">b · {bLabel}</th>
              <th className="px-4 py-2 font-medium">Δ pass rate</th>
              <th className="px-4 py-2 font-medium">Δ score</th>
            </tr>
          </thead>
          <tbody>
            {sortCriteriaByDrop(criteria).map((criterion) => (
              <tr
                key={criterion.endpointTestCriterionId ?? criterion.name}
                data-testid={`criterion-delta-${criterion.endpointTestCriterionId ?? criterion.name}`}
                className="border-t border-gray-100 dark:border-gray-700"
              >
                <td className="px-4 py-2 text-gray-900 dark:text-white">
                  {criterion.name}
                  {criterion.required === false && (
                    <span className="ml-1.5 text-xs text-gray-400">informational</span>
                  )}
                </td>
                <td className="px-4 py-2 text-xs text-gray-700 dark:text-gray-300">{sideText(criterion.a)}</td>
                <td className="px-4 py-2 text-xs text-gray-700 dark:text-gray-300">{sideText(criterion.b)}</td>
                <td className={`px-4 py-2 text-xs ${deltaClass(criterion.delta.passRate)}`}>
                  {deltaText(criterion.delta.passRate, 'rate')}
                </td>
                <td className={`px-4 py-2 text-xs ${deltaClass(criterion.delta.meanScore)}`}>
                  {deltaText(criterion.delta.meanScore, 'score')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default ComparisonCriteriaPanel;
